import { ProductCategory } from "@utils/constants";
import { TFunction } from "i18next";
import { DefaultThemeValueType } from "../../../styled";
import { Diamond } from "@src/types/DiamondDetail";
import { getTwoProductProperty, itemLocation } from "./productProperties";
import currency from "currency.js";
import { Linking } from "react-native";
import { capitalizeFirstLetter } from "../stringUtils";
type FormatNumberOptions = {
  precision?: number;
};

const formatNumberWithPrecision = (
  value: string | number | null | undefined,
  options: FormatNumberOptions = {}
): string | null => {
  if (value === null || value === undefined || value === "") return null;
  const { precision = 0 } = options;
  const parsed = parseFloat(String(value));
  if (isNaN(parsed) || !isFinite(parsed)) return "0";
  return parsed.toFixed(precision);
};

const addPercentage = (value?: string): string => {
  if (value === undefined || value === null) return;
  if (value === "") return "-";
  if (value === "-") return value;
  if (value.endsWith("%")) return value;
  return percent(value);
};

const removePercentage = (value: string): string => {
  if (!value) return;
  if (value === "-") return "";
  return value.endsWith("%") ? value.slice(0, -1) : value;
};

const calculateItemPrices = (item: any, key: any, value: any) => {
  let ntp, nppc, nd;
  switch (key) {
    case "tp":
      ntp = value;
      nppc = calculatePricePerCarat(
        value,
        formatNumberWithPrecision(item.size, { precision: 2 })
      );
      nd = calculateDiscount(item, nppc);
      break;
    case "ppc":
      ntp = calculateTotalPrice(
        value,
        formatNumberWithPrecision(item.size, { precision: 2 })
      );
      nppc = value;
      nd = calculateDiscount(item, nppc);
      break;
    case "discount":
      ntp = calculateTotalPriceOnDiscountChange(
        item,
        formatNumberWithPrecision(value, { precision: 2 })
      );
      nppc = calculatePricePerCarat(
        ntp || 0,
        formatNumberWithPrecision(item.size, { precision: 2 })
      );
      nd = value;
      break;
  }

  return {
    totalPrice: ntp,
    ppc: nppc,
    discount: nd,
  };
};

const calculateTotalPrice = (price: any, carat: any) => {
  return formatNumberWithPrecision(price * carat, { precision: 2 }) ?? "0";
};

const calculatePricePerCarat = (price: any, carat: any) => {
  return formatNumberWithPrecision(price / carat, { precision: 0 }) ?? "0";
};

const calculateTotalPriceOnDiscountChange = (item: any, nd: any) => {
  return (
    (parseFloat(item.total_sales_price) /
      (1 + parseFloat(item.discount_percent || 0) / 100)) *
    (1 + nd / 100)
  );
};

const calculateDiscount = (item: any, nppc: string) => {
  return (
    (parseFloat(nppc) *
      parseFloat(item.size) *
      (100 + parseFloat(item.discount_percent || 0)) -
      100 * parseFloat(item.total_sales_price)) /
    parseFloat(item.total_sales_price)
  );
};

const calculateParcelValues = (
  item: any,
  key: any,
  value: any,
  enableFourDecimalCarat: boolean
) => {
  let nqty, ntotalCarat;
  switch (key) {
    case "quantity":
      nqty = value;
      ntotalCarat =
        value === ""
          ? ""
          : formatNumberWithPrecision(item.size * nqty, {
              precision: enableFourDecimalCarat ? 4 : 3,
            });
      break;
    case "totalCarat":
      ntotalCarat =
        value === ""
          ? ""
          : formatNumberWithPrecision(value, {
              precision: enableFourDecimalCarat ? 4 : 3,
            });
      nqty =
        value === "" ? "" : Math.floor(Number(ntotalCarat) / Number(item.size));
      break;
  }

  return {
    quantity: nqty.toString(),
    totalCarat: ntotalCarat,
  };
};

const shortTitle = (diamond: any) => {
  return [
    diamond.shape_long,
    formatNumberWithPrecision(diamond.size, { precision: 2 }) + "ct",
    getColor(diamond, "long"),
  ].join(" ");
};

const getColor = (diamond: any, longShortName = "short") => {
  const isHasSkin = hasSkin(diamond);
  const isFancy = showFancyColor(diamond);
  const isLong = longShortName === "long";

  const overtone = isLong
    ? diamond.fancy_color_overtone_long
    : diamond.fancy_color_overtone_long;

  if (isFancy) {
    const intensity = isLong
      ? diamond.fancy_color_intensity_long
      : diamond.fancy_color_intensity_short;

    const dominantColor = isLong
      ? diamond?.bad_fields?.includes("fancy_color_dominant_color")
        ? diamond.fancy_color_dominant_color
        : diamond.fancy_color_long
      : diamond.fancy_color_short;

    const overtonePart = isHasSkin
      ? `(${overtone})`
      : `${overtone} ${
          diamond?.bad_fields?.includes("fancy_color_dominant_color")
            ? diamond.fancy_color_dominant_color
            : isLong
            ? diamond.fancy_color_long
            : diamond.fancy_color_short
        }`;

    let colorString = [intensity, `${dominantColor} ${overtonePart}`].join(" ");

    if (!diamond?.bad_fields?.includes("clarity")) {
      colorString = `${colorString} ${diamond.clarity}`;
    }

    return colorString;
  }

  return !diamond?.bad_fields?.includes("clarity")
    ? `${diamond.color} ${diamond.clarity}`
    : diamond.color;
};

const hasSkin = (diamond: any) => {
  return (
    diamond.fancy_color_overtone_long &&
    ["Champagne", "Cognac", "Chameleon"].includes(
      diamond.fancy_color_overtone_long
    )
  );
};

const showFancyColor = (diamond: any) => {
  if (diamond.is_fancy) {
    if (
      !diamond?.bad_fields?.includes("color") &&
      !diamond?.bad_fields?.includes("clarity")
    ) {
      return false;
    } else {
      return (
        diamond?.bad_fields?.includes("color") || // color is bad field
        (!diamond?.bad_fields?.includes("color") &&
          !diamond?.bad_fields?.includes("fancy_color_dominant_color")) || // color and dominant is good
        (diamond?.bad_fields?.includes("color") &&
          !diamond?.bad_fields?.includes("fancy_color_dominant_color")) || // color is good && dominant is bad
        (!diamond?.bad_fields?.includes("color") &&
          diamond?.bad_fields?.includes("fancy_color_dominant_color")) // color is bad && dominant is good
      );
    }
  } else return false;
};

const generateSavedSearchName = (search_params: any, t: TFunction) => {
  let options: { [k: string]: any } = {};
  let title_parts = [];

  // Stone Type
  if (
    search_params.stone_types &&
    Array.isArray(search_params.stone_types) &&
    search_params.stone_types?.length
  ) {
    options.stone_types = search_params?.stone_types || [];
    title_parts.push(options.stone_types.map(capitalizeFirstLetter).join(", "));
  }

  //is featured items
  options.featured =
    !!search_params?.featured && search_params?.featured === "true"
      ? "true"
      : "false";

  //Shape
  if (search_params.shapes && search_params.shapes?.length) {
    options.shapes = search_params?.shapes || [];
    title_parts.push(options.shapes.join(", "));
  }

  //Carat Weight
  let carat_title = "";
  if (search_params?.size_from || search_params?.minCarat) {
    options.minCarat = search_params?.size_from ?? search_params?.minCarat;
    carat_title += options.minCarat + "ct.";
  }
  if (search_params?.size_to || search_params?.maxCarat) {
    options.maxCarat = search_params?.size_to ?? search_params?.maxCarat;
    carat_title += " - " + options.maxCarat + "ct.";
  }
  title_parts.push(carat_title);

  // Color (simple)
  if (search_params?.color_from || search_params?.simpleColors?.length) {
    let color_title = "";
    options.simpleColors = [];
    options.simpleColors.push(search_params?.color_from ?? []);
    color_title += search_params?.color_from;
    if (search_params?.color_to) {
      if (!options.simpleColors) options.simpleColors = [];
      options.simpleColors.push(search_params?.color_to ?? []);
      color_title += " - " + search_params?.color_to;
    }
    if (search_params.simpleColors && search_params.simpleColors?.length) {
      options.simpleColors = search_params?.simpleColors || [];
      color_title = options.simpleColors.join(" - ");
    }
    if (color_title) title_parts.push(color_title);
  }

  // Fancy Color
  if (
    (search_params.fancy_colors && search_params.fancy_colors?.length) ||
    (search_params?.fancyColors1 && search_params.fancyColors1?.length)
  ) {
    options.fancyColors1 =
      search_params?.fancy_colors || search_params.fancyColors1 || [];
    let color_title =
      t("search.diamond.fancyColor.text") +
      ": " +
      options.fancyColors1.join(" - ");
    title_parts.push(color_title);
  }

  //Fancy Color Overtones
  if (
    (search_params.fancy_color_overtones &&
      search_params.fancy_color_overtones?.length) ||
    (search_params.fancyColorsOvertones &&
      search_params.fancyColorsOvertones?.length)
  ) {
    options.fancyColorsOvertones =
      search_params?.fancy_color_overtones ||
      search_params.fancyColorsOvertones ||
      [];
    let overtone_color_title =
      t("search.diamond.fcOvertone.text") +
      ": " +
      options.fancyColorsOvertones.join(", ");
    title_parts.push(overtone_color_title);
  }

  //Fancy Color Intensity
  if (
    search_params.fancy_color_intensity_from &&
    search_params.fancy_color_intensity_from?.length
  ) {
    options.fancy_color_intensity_from =
      search_params.fancy_color_intensity_from;
    options.fancy_color_intensity_to = search_params.fancy_color_intensity_to;
    title_parts.push(
      t("search.diamond.fcIntensity.text", "Fancy Color Intensity") +
        ": " +
        options.fancy_color_intensity_from +
        " - " +
        options.fancy_color_intensity_to
    );
  }

  // Clarity
  options.clarities = [];
  if (search_params?.clarity_from) {
    options.clarities.push(search_params?.clarity_from);
  }
  if (search_params?.clarity_to) {
    options.clarities.push(search_params?.clarity_to);
  }
  if (search_params?.clarities?.length)
    options.clarities = search_params?.clarities ?? [];
  if (options?.clarities?.length)
    title_parts.push(options?.clarities.join(" - "));

  // Cut
  options.cutGrades = [];
  if (search_params?.cut_from) {
    options.cutGrades.push(search_params?.cut_from);
  }
  if (search_params?.cut_to) {
    options.cutGrades.push(search_params?.cut_to);
  }
  if (search_params.cutGrades?.length)
    options.cutGrades = search_params.cutGrades;
  if (options.cutGrades.length) {
    let cutTitle =
      t("search.diamond.cut.text") + ": " + options.cutGrades.join(" - ");
    title_parts.push(cutTitle);
  }

  // Polish
  options.polishes = [];
  if (search_params?.polish_from) {
    options.polishes.push(search_params?.polish_from);
  }
  if (search_params?.polish_to) {
    options.polishes.push(search_params?.polish_to);
  }
  if (search_params.polishes?.length) options.polishes = search_params.polishes;
  if (options.polishes?.length) {
    let polishTitle =
      t("search.diamond.pol.text") + ": " + options.polishes.join(" - ");
    title_parts.push(polishTitle);
  }

  // Symmetry
  options.symmetries = [];
  if (search_params?.polish_from) {
    options.symmetries.push(search_params?.symmetry_from);
  }
  if (search_params?.polish_to) {
    options.symmetries.push(search_params?.symmetry_to);
  }
  if (search_params.symmetries?.length)
    options.symmetries = search_params.symmetries;
  if (options.symmetries?.length) {
    let symTitle =
      t("search.diamond.sym.text") + ": " + options.symmetries.join(" - ");
    title_parts.push(symTitle);
  }

  // Fluorescence
  if (search_params?.fluorescence_intensities?.length) {
    options.fluorescences = search_params?.fluorescence_intensities || [];
  } else if (search_params?.fluorescences?.length) {
    options.fluorescences = search_params?.fluorescences || [];
  }
  if (options?.fluorescences?.length > 0) {
    let flour_values =
      t("search.diamond.flour.text") + ": " + options.fluorescences.join(", ");
    title_parts.push(flour_values);
  }

  // Lab
  if (search_params?.labs && search_params?.labs?.length) {
    options.labs = search_params?.labs || [];
    let labTitle =
      t("search.diamond.lab.text") + ": " + options.labs.join(", ");
    title_parts.push(labTitle);
  }

  //Depth
  let depth_title = "";
  if (
    (search_params?.depth_percent_from &&
      search_params?.depth_percent_from >= 0) ||
    (search_params?.minDepth && search_params?.minDepth >= 0)
  ) {
    options.minDepth =
      search_params?.depth_percent_from ?? search_params.minDepth;
    depth_title += t("search.diamond.depth.text") + ": " + options.minDepth;
  }
  if (
    (search_params?.depth_percent_to && search_params?.depth_percent_to >= 0) ||
    (search_params?.maxDepth && search_params?.maxDepth >= 0)
  ) {
    options.maxDepth =
      search_params?.depth_percent_to ?? search_params?.maxDepth;
    depth_title += " - " + percent(options.maxDepth);
  }
  if (depth_title) title_parts.push(depth_title);

  //table title
  let table_title = "";
  if (
    (search_params?.table_percent_from &&
      search_params?.table_percent_from >= 0) ||
    (search_params?.minTable && search_params?.minTable >= 0)
  ) {
    options.minTable =
      search_params?.table_percent_from ?? search_params?.minTable;
    table_title += t("search.diamond.table.text") + ": " + options.minTable;
  }
  if (
    (search_params?.table_percent_to && search_params?.table_percent_to >= 0) ||
    (search_params?.maxTable && search_params?.maxTable >= 0)
  ) {
    options.maxTable =
      search_params?.table_percent_to ?? search_params?.maxTable;
    table_title += " - " + percent(options.maxTable);
  }
  if (table_title) title_parts.push(table_title);

  // Meas Length
  let measLengthTitle = "";
  let measLengthLabel = t("search.diamond.measLength.text");
  if (search_params?.meas_length_from || search_params?.meas_length_to) {
    options.meas_length_from = search_params?.meas_length_from ?? "0";
    measLengthTitle = measLengthLabel + ": " + options.meas_length_from;
  }
  if (search_params?.meas_length_to) {
    options.meas_length_to = search_params?.meas_length_to;
    measLengthTitle += " - " + options.meas_length_to;
  }
  if (measLengthTitle) title_parts.push(measLengthTitle);

  // Meas Width
  let measWidthTitle = "";
  let measWidthLabel = t("search.diamond.measWidth.text");
  if (search_params?.meas_width_from || search_params?.meas_width_to) {
    options.meas_width_from = search_params?.meas_width_from ?? "0";
    measWidthTitle = measWidthLabel + ": " + options.meas_width_from;
  }
  if (search_params?.meas_width_to) {
    options.meas_width_to = search_params?.meas_width_to;
    measWidthTitle += " - " + options.meas_width_to;
  }
  if (measWidthTitle) title_parts.push(measWidthTitle);

  // meas_depth
  if (search_params?.meas_height_from || search_params?.meas_height_to) {
    options.meas_height_from = search_params?.meas_height_from ?? "0";
  } else if (search_params?.minDepth || search_params?.maxDepth) {
    options.meas_height_from = search_params?.minDepth ?? "0";
  }
  if (search_params?.meas_height_to) {
    options.meas_height_to = search_params?.meas_height_to;
  } else if (search_params?.maxDepth) {
    options.meas_height_to = search_params?.maxDepth;
  }
  if (options.meas_height_from && options.meas_height_to) {
    let measDepthTitle =
      t("search.diamond.measdepth.text") +
      ": " +
      options.meas_height_from +
      " - " +
      options.meas_height_to;
    title_parts.push(measDepthTitle);
  }

  // Meas Ratio
  if (search_params?.meas_ratio_from || search_params?.meas_ratio_to) {
    options.meas_ratio_from = search_params?.meas_ratio_from ?? "0";
  }
  if (search_params?.meas_ratio_to) {
    options.meas_ratio_to = search_params?.meas_ratio_to;
  }
  if (search_params?.meas_ratio_from && search_params?.meas_ratio_to) {
    let measTitle =
      t("search.diamond.measRatio.text") +
      ": " +
      options.meas_ratio_from +
      " - " +
      options.meas_ratio_to;
    title_parts.push(measTitle);
  }

  //Total Price
  let budget_title = "";
  let totalPriceLabel = t("search.diamond.total.text");
  if (
    (search_params?.price_total_from && search_params?.price_total_from) ||
    (search_params?.minBudget && search_params?.minBudget)
  ) {
    options.minBudget =
      search_params?.price_total_from ?? search_params?.minBudget;
    budget_title += totalPriceLabel + ": " + options.minBudget;
  }
  if (
    (search_params?.price_total_to && search_params?.price_total_to >= 0) ||
    (search_params?.maxBudget && search_params?.maxBudget >= 0)
  ) {
    options.maxBudget =
      search_params?.price_total_to ?? search_params?.maxBudget;
    budget_title += " - " + options.maxBudget;
  }
  if (budget_title) title_parts.push(budget_title);

  // Price per Carat
  let price_per_carat_title = "";
  let ppcLabel = t("search.diamond.ppc.text");
  if (search_params?.price_per_carat_from || search_params?.minPricePerCt) {
    options.minPricePerCt =
      search_params?.price_per_carat_from ?? search_params?.minPricePerCt;
    price_per_carat_title += ppcLabel + ": " + options.minPricePerCt;
  }
  if (search_params?.price_per_carat_to || search_params?.maxPricePerCt) {
    options.maxPricePerCt =
      search_params?.price_per_carat_to ?? search_params?.maxPricePerCt;
    price_per_carat_title += " - " + options.maxPricePerCt;
  }
  if (price_per_carat_title) title_parts.push(price_per_carat_title);

  // Location
  if (search_params?.location_for) {
    options.location_for = search_params?.location_for || "";
    title_parts.push(options.location_for);
  }

  //Location
  if (
    search_params?.vendor_locations &&
    search_params?.vendor_locations?.length
  ) {
    options.vendor_locations = search_params?.vendor_locations || [];
    let locationTitle =
      t("search.diamond.itemLoc.text") +
      (Boolean(search_params?.exclude_locations) === true
        ? " " + t("search.diamond.excluded.text", "(Excluded)")
        : "") +
      ": " +
      options.vendor_locations.join(", ");
    title_parts.push(locationTitle);
  }

  // Supplier
  if (
    (search_params?.company_names && search_params?.company_names?.length) ||
    (search_params?.companyNames && search_params?.companyNames?.length)
  ) {
    options.companyNames =
      search_params?.company_names || search_params?.companyNames || [];
    let supplerTitle =
      t("search.diamond.supplier.text") +
      (Boolean(search_params?.exclude_suppliers) === true
        ? " " + t("search.diamond.excluded.text", "(Excluded)")
        : "") +
      ": " +
      options.companyNames.join(",");
    title_parts.push(supplerTitle);
  }

  //Countries
  if (search_params?.countries && search_params?.countries?.length) {
    options.countries = search_params?.countries || [];
    title_parts.push(options.countries.join(", "));
  }

  //Cities
  if (search_params?.cities && search_params?.citie?.length) {
    options.cities = search_params?.cities || [];
    title_parts.push(options.cities.join(", "));
  }

  // Growth
  if (search_params?.growth_type && search_params?.growth_type?.length) {
    options.growth_type = search_params?.growth_type || [];
    let growthTitle =
      t("search.diamond.growth.text") + ": " + options.growth_type.join(",");
    title_parts.push(growthTitle);
  }

  // Total or Price per carat
  options.total_or_price_per_carat =
    search_params?.total_or_price_per_carat || "";

  //Argyle
  options.argyle = search_params?.argyle || {};

  //Group IDs
  if (search_params?.group_ids && search_params?.group_ids?.length) {
    options.group_ids = search_params?.group_ids || [];
    title_parts.push(options.group_ids.join(", "));
  }

  //Certifications
  if (search_params?.certifications && search_params?.certifications?.length) {
    options.certifications = search_params?.certifications || [];
    title_parts.push(options.certifications.join(", "));
  }

  //parcel
  if (search_params?.parcel) {
    options.parcel = search_params?.parcel;
    title_parts.push(t("search.diamond.parcel.text"));
  }

  //3X
  if (search_params?.three_x && search_params?.three_x === true) {
    options.three_x = true;
    title_parts.push(t("search.diamond.3x.text"));
  }

  //Hearts and Arrows (H&A)
  if (
    search_params?.hearts_and_arrows &&
    search_params?.hearts_and_arrows === "Hearts and Arrows"
  ) {
    options.hearts_and_arrows = search_params?.hearts_and_arrows;
    title_parts.push(t("search.diamond.h&a.text"));
  }

  //Enhancements
  if (search_params?.enhancements && search_params?.enhancements?.length) {
    options.enhancements = search_params?.enhancements || [];
    if (options.enhancements.indexOf("HPHT") !== -1) {
      options.enhancements[options.enhancements.indexOf("HPHT")] =
        "Color Enhanced";
    }
    title_parts.push(options.enhancements.join(", "));
  }

  //Eye Clean
  if (search_params?.eye_clean) {
    options.eye_clean = search_params.eye_clean;
    if (Array.isArray(search_params?.eye_clean)) {
      title_parts.push(options.eye_clean.join(", "));
    } else {
      title_parts.push(t("search.diamond.eyeClean.text", "Eye Clean"));
    }
  }

  //No BGM
  if (search_params?.no_bgm) {
    options.no_bgm = search_params?.no_bgm;
    title_parts.push(t("search.diamond.noBgm.text"));
  }

  //3VG+
  if (search_params?.three_vg_plus) {
    options.three_vg_plus = search_params?.three_vg_plus;
    title_parts.push(t("search.diamond.3vgPlus.text"));
  }

  //8X
  if (search_params?.eight_x) {
    options.eight_x = search_params?.eight_x;
    title_parts.push(t("search.diamond.8x.text"));
  }

  //Inclusion Pattern
  if (search_params?.inclusion_pattern) {
    options.inclusion_pattern = search_params?.inclusion_pattern;
  }

  //Intensity Black Code
  if (search_params?.intensity_black_code) {
    options.intensity_black_code = search_params?.intensity_black_code;
  }

  //Inclusion Open
  if (search_params?.inclusion_open) {
    options.inclusion_open = search_params?.inclusion_open;
  }

  //With Images
  if (search_params?.with_images) {
    options.with_images = search_params?.with_images;
  }

  //Certified Pairs
  if (search_params?.certified_pairs) {
    options.certified_pairs = search_params?.certified_pairs;
  }

  //With Available Items
  if (search_params?.with_available_items) {
    options.with_available_items = search_params?.with_available_items;
  }

  //Pair
  if (search_params?.pair) {
    options.pair = search_params?.pair;
  }

  //Discount Percent
  if (search_params?.discount_percent_from) {
    options.discount_percent_from = search_params?.discount_percent_from;
  }
  if (search_params?.discount_percent_to) {
    options.discount_percent_to = search_params?.discount_percent_to;
  }

  let crownTitle = "";
  let crownTitleLabel = t("search.diamond.crown.text", "Crown");
  // Crown Height
  if (
    search_params?.crown_percent_from &&
    search_params?.crown_percent_from >= 0
  ) {
    options.crown_percent_from = search_params?.crown_percent_from;
    crownTitle += crownTitleLabel + ": " + percent(options.crown_percent_from);
  }
  if (search_params?.crown_percent_to && search_params?.crown_percent_to >= 0) {
    options.crown_percent_to = search_params?.crown_percent_to;
    crownTitle +=
      crownTitle === ""
        ? crownTitleLabel + ": 0% - " + percent(options.crown_percent_to)
        : " - " + percent(options.crown_percent_to);
  }

  // Crown Angle
  if (search_params?.crown_angle_from && search_params?.crown_angle_from >= 0) {
    options.crown_angle_from = search_params?.crown_angle_from;
    crownTitle +=
      crownTitle === ""
        ? crownTitleLabel + ": " + options.crown_angle_from + "°"
        : ", " + options.crown_angle_from + "°";
  }
  if (search_params?.crown_angle_to && search_params?.crown_angle_to >= 0) {
    options.crown_angle_to = search_params?.crown_angle_to;
    crownTitle +=
      crownTitle === ""
        ? "0° - " + options.crown_angle_to + "°"
        : search_params?.crown_angle_from === undefined
        ? ", 0° - " + options.crown_angle_to + "°"
        : " - " + options.crown_angle_to + "°";
  }
  if (crownTitle) title_parts.push(crownTitle);

  let pavilionTitle = "";
  let pavilionTitleLabel = t("search.diamond.pavilion.text", "Pavilion");
  // Pavilion Depth
  if (
    search_params?.pavilion_percent_from &&
    search_params?.pavilion_percent_from > 0
  ) {
    options.pavilion_percent_from = search_params?.pavilion_percent_from;
    pavilionTitle +=
      pavilionTitleLabel + ": " + percent(options.pavilion_percent_from);
  }
  if (
    search_params?.pavilion_percent_to &&
    search_params?.pavilion_percent_to > 0
  ) {
    options.pavilion_percent_to = search_params?.pavilion_percent_to;
    pavilionTitle +=
      pavilionTitle === ""
        ? pavilionTitleLabel + ": 0% - " + percent(options.pavilion_percent_to)
        : " - " + percent(options.pavilion_percent_to);
  }

  // Pavilion Angle
  if (
    search_params?.pavilion_angle_from &&
    search_params?.pavilion_angle_from > 0
  ) {
    options.pavilion_angle_from = search_params?.pavilion_angle_from;
    pavilionTitle +=
      pavilionTitle === ""
        ? pavilionTitleLabel + ": " + options.pavilion_angle_from + "°"
        : ", " + options.pavilion_angle_from + "°";
  }
  if (
    search_params?.pavilion_angle_to &&
    search_params?.pavilion_angle_to > 0
  ) {
    options.pavilion_angle_to = search_params?.pavilion_angle_to;
    pavilionTitle +=
      pavilionTitle === ""
        ? "0° - " + options.pavilion_angle_to + "°"
        : search_params?.pavilion_angle_from === undefined
        ? ", 0° - " + options.pavilion_angle_to + "°"
        : " - " + options.pavilion_angle_to + "°";
  }
  if (pavilionTitle) title_parts.push(pavilionTitle);

  //Exclude Location & Supplier
  if (search_params?.exclude_locations) {
    options.exclude_locations = search_params?.exclude_locations;
  }

  if (search_params?.exclude_suppliers) {
    options.exclude_suppliers = search_params?.exclude_suppliers;
  }

  return { title: title_parts.filter(Boolean).join(" | "), options: options };
};

function generateGemstoneSavedSearchName(searchParams: any, t: TFunction) {
  let titleParts = [];
  let options: { [k: string]: any } = {};

  // Stone Type
  if (
    searchParams.stone_types &&
    Array.isArray(searchParams.stone_types) &&
    searchParams.stone_types?.length
  ) {
    options.stone_types = searchParams?.stone_types || [];
    titleParts.push(options.stone_types.map(capitalizeFirstLetter).join(", "));
  }

  //Type
  if (searchParams?.gem_type) {
    options.gem_type = searchParams.gem_type;
    titleParts.push(searchParams?.gem_type);
  }

  //Color
  if (searchParams?.color) {
    options.color = searchParams.color;
    titleParts.push(
      t("search.gemstone.color.text") + ": " + searchParams?.color
    );
  }

  //Shape
  if (searchParams?.shapes && searchParams?.shapes.length) {
    options.shapes = searchParams.shapes;
    titleParts.push(searchParams?.shapes.join(", "));
  }

  //Carat
  if (
    searchParams?.size_from ||
    searchParams?.minCarat ||
    searchParams?.size_to ||
    searchParams?.maxCarat
  ) {
    let carat_title = "";
    options.minCarat = searchParams.size_from || searchParams?.minCarat;
    carat_title += options.minCarat + "ct.";
    if (searchParams?.size_to || searchParams?.maxCarat) {
      options.maxCarat = searchParams.size_to || searchParams?.maxCarat;
      carat_title += " - " + options.maxCarat + "ct.";
    }
    titleParts.push(carat_title);
  }
  // if (searchParams?.minCarat || searchParams?.maxCarat) {
  //   searchParams.minCarat = searchParams?.minCarat ? searchParams?.minCarat : 0;
  //   let carat_title = "";
  //   carat_title += "PPC: " + searchParams?.minCarat + "ct.";
  //   if (searchParams?.maxCarat) {
  //     carat_title += " - " + searchParams?.maxCarat + "ct.";
  //   }
  //   titleParts.push(carat_title);
  // }

  //Lab
  if (searchParams?.labs && searchParams?.labs.length > 0) {
    options.labs = searchParams.labs;
    titleParts.push(searchParams?.labs.join(", "));
  }

  //Ratio
  if (searchParams?.meas_ratio_from || searchParams?.meas_ratio_to) {
    searchParams.meas_ratio_from = searchParams?.meas_ratio_from
      ? searchParams?.meas_ratio_from
      : 0;
    let meas_ratio = "";
    options.meas_ratio_from = searchParams?.meas_ratio_from;
    meas_ratio +=
      t("search.gemstone.ratio.text") + ": " + searchParams?.meas_ratio_from;
    if (searchParams?.meas_ratio_to) {
      options.meas_ratio_to = searchParams?.meas_ratio_to;
      meas_ratio += " - " + searchParams?.meas_ratio_to;
    }
    titleParts.push(meas_ratio);
  }

  //Meas Length
  if (searchParams?.meas_length_from || searchParams?.meas_length_to) {
    searchParams.meas_length_from = searchParams?.meas_length_from
      ? searchParams?.meas_length_from
      : 0;
    let meas_length = "";
    options.meas_length_from = searchParams?.meas_length_from;
    meas_length +=
      t("search.gemstone.measLength.text") +
      ": " +
      searchParams?.meas_length_from;
    if (searchParams?.meas_length_to) {
      options.meas_length_to = searchParams?.meas_length_to;
      meas_length += " - " + searchParams?.meas_length_to;
    }
    titleParts.push(meas_length);
  }

  //Meas Width
  if (searchParams?.meas_width_from || searchParams?.meas_width_to) {
    searchParams.meas_width_from = searchParams?.meas_width_from
      ? searchParams?.meas_width_from
      : 0;
    let meas_width = "";
    options.meas_width_from = searchParams?.meas_width_from;
    meas_width +=
      t("search.gemstone.measWidth.text") +
      ": " +
      searchParams?.meas_width_from;
    if (searchParams?.meas_width_to) {
      options.meas_width_to = searchParams?.meas_width_to;
      meas_width += " - " + searchParams?.meas_width_to;
    }
    titleParts.push(meas_width);
  }

  //Meas Depth
  if (searchParams?.meas_depth_from || searchParams?.meas_depth_to) {
    searchParams.meas_depth_from = searchParams?.meas_depth_from
      ? searchParams?.meas_depth_from
      : 0;
    let meas_depth = "";
    options.meas_depth_from = searchParams?.meas_depth_from;
    meas_depth +=
      t("search.gemstone.measDepth.text") +
      ": " +
      searchParams?.meas_depth_from;
    if (searchParams?.meas_depth_to) {
      options.meas_depth_to = searchParams?.meas_depth_to;
      meas_depth += " - " + searchParams?.meas_depth_to;
    }
    titleParts.push(meas_depth);
  }

  // Total or Price per carat
  options.total_or_price_per_carat =
    searchParams?.total_or_price_per_carat || "";

  //PPC
  if (searchParams?.total_or_price_per_carat) {
    //Total Price
    if (
      searchParams?.total_or_price_per_carat === "total_sales_price" &&
      (searchParams?.price_total_from ||
        searchParams?.minBudget ||
        searchParams?.price_total_to ||
        searchParams?.maxBudget)
    ) {
      let budget = "";

      options.minBudget =
        searchParams.price_total_from || searchParams.minBudget || 0;
      budget += t("search.gemstone.total.text") + ": " + options.minBudget;
      if (searchParams?.price_total_to || searchParams?.maxBudget) {
        options.maxBudget =
          searchParams.price_total_to || searchParams?.maxBudget || 0;
        budget += " - " + options.maxBudget;
      }
      titleParts.push(budget);
    } else if (
      // Price Per Carat
      searchParams?.price_per_carat_from ||
      searchParams?.price_per_carat_to ||
      searchParams?.minPricePerCt ||
      searchParams?.maxPricePerCt
    ) {
      let budget = "";
      options.minPricePerCt =
        searchParams?.price_per_carat_from || searchParams?.minPricePerCt || 0;
      budget += t("search.gemstone.ppc.text") + ": " + options.minPricePerCt;
      if (searchParams?.price_per_carat_to) {
        options.maxPricePerCt =
          searchParams?.price_per_carat_to || searchParams?.maxPricePerCt || 0;
        budget += " - " + options.maxPricePerCt;
      }
      titleParts.push(budget);
    }
  }

  //Location
  if (
    (searchParams?.vendor_locations &&
      searchParams?.vendor_locations.length > 0) ||
    (searchParams?.vendorLocations && searchParams?.vendorLocations.length > 0)
  ) {
    options.vendorLocations =
      searchParams.vendor_locations || searchParams.vendorLocations;
    titleParts.push(
      t("search.gemstone.itemLoc.text") +
        ": " +
        options.vendorLocations.join(", ")
    );
  }

  //Supplier
  if (
    (searchParams.company_names && searchParams.company_names.length > 0) ||
    (searchParams.companyNames && searchParams.companyNames.length > 0)
  ) {
    options.companyNames =
      searchParams.company_names || searchParams.companyNames;
    titleParts.push(
      t("search.gemstone.supplier.text") +
        ": " +
        options.companyNames.join(", ")
    );
  }

  //Treatment
  if (
    searchParams?.treatment &&
    typeof searchParams?.treatment === "string" &&
    searchParams?.treatment?.toLowerCase() !== "all" &&
    searchParams?.treatment?.toLowerCase() !== ""
  ) {
    options.treatment = searchParams.treatment;
    titleParts.push(
      t("search.gemstone.treat.text") + ": " + searchParams?.treatment
    );
  } else {
    titleParts.push(t("search.gemstone.treat.text") + ": All");
  }

  //Origin
  if (
    searchParams?.country_of_origin &&
    searchParams?.country_of_origin.length
  ) {
    options.country_of_origin = searchParams.country_of_origin;
    titleParts.push(
      t("search.gemstone.origin.text") +
        ": " +
        searchParams?.country_of_origin.join(", ")
    );
  }

  //Discount
  if (
    searchParams?.discount_percent_from ||
    searchParams?.discount_percent_to
  ) {
    searchParams.discount_percent_from = searchParams?.discount_percent_from
      ? searchParams?.discount_percent_from
      : 0;
    let Discount_percent = "";
    options.discount_percent_from = searchParams?.discount_percent_from;
    Discount_percent +=
      t("search.gemstone.discount.text") +
      ": " +
      searchParams?.discount_percent_from;
    if (searchParams?.discount_percent_to) {
      options.discount_percent_to = searchParams?.discount_percent_to;
      Discount_percent += " - " + searchParams?.discount_percent_to;
    }
    titleParts.push(Discount_percent);
  }

  //Certified Pairs
  if (searchParams?.certified_pairs) {
    options.certified_pairs = searchParams?.certified_pairs;
  }

  //Pair
  if (searchParams?.pair) {
    options.pair = searchParams?.pair;
    titleParts.push(searchParams?.pair.includes("other") ? "single" : "match");
  }

  //with images
  if (searchParams?.with_images) {
    options.with_images = searchParams?.with_images;
  }

  //with available items
  if (searchParams?.with_available_items) {
    options.with_available_items = searchParams?.with_available_items;
  }

  options.totalPrice = searchParams.totalPrice;
  options.itemType = 2;

  return {
    title: titleParts.filter(Boolean).join(" | ").replace(/,\s*$/, ""),
    options: options,
  };
}

function generateJewelrySavedSearchName(
  searchParams: any,
  t: TFunction,
  jewelryFilters: any,
  collection: any,
  sub_collection: any = []
) {
  const types = jewelryFilters.find((filter: any) =>
    filter?.payload_name?.includes("jewelry_styles")
  )?.options;

  let title = [];
  let options: { [k: string]: any } = {};
  // Type
  let jewelryStyles = "";
  if (searchParams?.jewelry_styles) {
    let formattedJewelryStyles = { jewelry_styles: [] };

    const styles = Array.isArray(searchParams.jewelry_styles)
      ? searchParams.jewelry_styles
      : Object.keys(searchParams.jewelry_styles);

    for (const style of styles) {
      const attributes = Array.isArray(searchParams.jewelry_styles)
        ? searchParams[style] || []
        : searchParams.jewelry_styles[style] || [];
      const validAttributes = attributes.filter(
        (attr: string) => attr.trim().length > 0
      );

      formattedJewelryStyles.jewelry_styles.push(style as never);

      if (validAttributes.length > 0) {
        formattedJewelryStyles[style as keyof typeof formattedJewelryStyles] =
          validAttributes as never;
      }

      const styleLabel = types?.find((type: any) =>
        style
          ? type?.search_payload?.includes(style)
          : type?.search_payload?.length === 0
      )?.label;

      jewelryStyles = t(styleLabel);
    }

    Object.assign(options, formattedJewelryStyles);
    title.push(jewelryStyles);
  }

  //Sub Type
  let subTypes = [];
  if (
    searchParams?.jewelry_styles &&
    searchParams?.jewelry_styles?.[jewelryStyles]?.length
  ) {
    subTypes = searchParams?.jewelry_styles?.[jewelryStyles];
    title.push(subTypes.join(", "));
  }

  // Metals
  if (searchParams?.metals && searchParams?.metals.length > 0) {
    options.metals = searchParams.metals;
    const entries = Object.entries(searchParams.metals[0]);
    const formattedItems = entries.map(([key, values]) => {
      if ((values as any[]).length > 0 && (values as any[])[0] !== "") {
        return `${key} (${(values as any[]).join("K, ")}K)`;
      }
      return key;
    });
    title.push(formattedItems.join(", "));
  }

  // Center Stone Weight
  let sizeTitle = "";
  if (searchParams?.size_from || searchParams?.minCarat) {
    options.minCarat = searchParams.size_from ?? searchParams?.minCarat;
    sizeTitle +=
      t("search.jewelry.centerStoneWt.text") + ": " + options.minCarat + "ct.";
    sizeTitle +=
      (searchParams?.size_from || searchParams?.minCarat) &&
      (searchParams?.size_to || searchParams?.maxCarat)
        ? " - "
        : "";
    if (searchParams?.size_to || searchParams?.maxCarat) {
      options.maxCarat = searchParams.size_to ?? searchParams?.maxCarat;
      sizeTitle += options.maxCarat + "ct.";
    }
    title.push(sizeTitle);
  }

  // Total Price
  let budget = "";
  if (searchParams?.price_total_from || searchParams?.minBudget) {
    options.minBudget =
      searchParams?.price_total_from ?? searchParams?.minBudget;
    budget +=
      t("search.jewelry.total.text") +
      ": " +
      options.minBudget?.toLocaleString();
    budget +=
      (searchParams?.price_total_from || searchParams?.minBudge) &&
      (searchParams?.price_total_to || searchParams?.maxBudget)
        ? " - "
        : "";
    if (searchParams?.price_total_to || searchParams?.maxBudget) {
      options.maxBudget =
        searchParams?.price_total_to ?? searchParams?.maxBudget;
      budget += options.maxBudget?.toLocaleString();
    }
    title.push(budget);
  }

  // Locations
  let vendor_locations = "";
  if (
    (searchParams?.vendor_locations && searchParams?.vendor_locations.length) ||
    (searchParams?.vendorLocations && searchParams?.vendorLocations.length)
  ) {
    options.vendorLocations =
      searchParams.vendor_locations ?? searchParams?.vendorLocations;
    vendor_locations =
      t("search.jewelry.itemLoc.text") +
      ": " +
      options.vendorLocations.join(", ");
    title.push(vendor_locations);
  }

  // Company Names
  let company_names = "";
  if (
    (searchParams?.company_names && searchParams?.company_names.length) ||
    (searchParams?.companyNames && searchParams?.companyNames.length)
  ) {
    options.companyNames =
      searchParams?.company_names ?? searchParams?.companyNames;
    company_names =
      t("search.jewelry.supplier.text") +
      ": " +
      options.companyNames.join(", ");
    title.push(company_names);
  }

  // Brands
  let brands = "";
  if (searchParams?.brands && searchParams?.brands.length > 0) {
    brands =
      t("search.jewelry.brand.text") + ": " + searchParams?.brands.join(", ");
    options.brands = searchParams?.brands;
    title.push(brands);
  }

  // Stone Type
  // if (searchParams?.stone_type && searchParams?.stone_type.length > 0) {
  //   title.push(searchParams?.stone_type);
  // }

  // Collection Names - prioritize the collection parameter passed from Redux store
  if (collection && collection.length > 0) {
    options.collection_names = collection;
  } else if (
    searchParams?.collection_names &&
    searchParams?.collection_names.length > 0
  ) {
    options.collection_names = searchParams.collection_names;
  }

  if (sub_collection && sub_collection.length > 0) {
    options.sub_collection = sub_collection;
  }

  options.itemType = 2;
  if (searchParams.sub_type) options.sub_type = searchParams.sub_type;

  Object.keys(searchParams).forEach((key) => {
    if (!(key in options)) {
      options[key] = searchParams[key];
    }
  });

  return { title: title.filter(Boolean).join(" | "), options: options };
}

const getItemTypeColor = (theme: DefaultThemeValueType, itemType: string) => {
  switch (itemType) {
    case ProductCategory.DIAMOND:
      return theme["diam-01"];
    case ProductCategory.GEMSTONE:
      return theme["gem-01"];
    case ProductCategory.JEWELRY:
      return theme["jewelry-01"];
    default:
      return theme["lab-01"];
  }
};

const handleDeepLink = (deepLink: string) => {
  let deepLinkUrl = deepLink;
  deepLinkUrl += deepLinkUrl.includes("?")
    ? "&fromHomePage=true"
    : "?fromHomePage=true";

  Linking.openURL(deepLinkUrl);
};

const getProductId = (productType: ProductCategory) => {
  switch (productType) {
    case ProductCategory.DIAMOND:
      return 0;
    case ProductCategory.LAB_GROWN_DIAMOND:
      return 1;
    case ProductCategory.GEMSTONE:
      return 2;
    case ProductCategory.JEWELRY:
      return 3;
    case ProductCategory.LABGROWN_JEWELRY:
      return 4;
  }
};

const addComma = (num: string) => {
  num += "";
  var x = num.split(".");
  var x1 = x[0];
  var x2 = x.length > 1 ? "." + x[1] : "";
  var rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, "$1" + "," + "$2");
  }
  return x1 + x2;
};

const valueOrDash = (val?: any) => (val ? String(val) : "-");

const percent = (val?: any) => (val ? `${val}%` : "-");

const formatCurrency = (
  value: string | number | null | undefined,
  { symbol = "$", precision = 2 }: { symbol?: string; precision?: number } = {}
): string => {
  if (value === null || value === undefined || value === "")
    return `${symbol}0`;

  return currency(value, {
    symbol,
    precision,
  }).format();
};

const getDiamondValue = (
  key: string,
  item?: Diamond,
  userCurrency?: string
) => {
  switch (key) {
    case "Stock":
      return item?.stock_num ? "VDB-" + item?.stock_num : "-";
    case "Cut":
      return valueOrDash(item?.cut_short);
    case "Pol":
      return valueOrDash(item?.polish_short);
    case "Sym":
      return valueOrDash(item?.symmetry_short);
    case "Fluor":
      return valueOrDash(item?.fluorescence_intensity_short);
    case "Depth":
      return percent(item?.depth_percent);
    case "Table":
      return percent(item?.table_percent);
    case "Crown":
      return getTwoProductProperty(
        item?.crown_height ?? "",
        "%",
        item?.crown_angle ?? "",
        "°"
      );
    case "Pavilion":
      return getTwoProductProperty(
        item?.pavilion_depth ?? "",
        "%",
        item?.pavilion_angle ?? "",
        "°"
      );
    case "Meas":
      return valueOrDash(item?.measurement);
    case "Ratio":
      return valueOrDash(item?.meas_ratio);
    case "Girdle":
      return valueOrDash(item?.girdle_min_short);
    case "Location":
      return item?.locations ? item?.locations?.[0]?.location_name : "-";
    case "SupplierName":
      return valueOrDash(item?.vendor_name);
    case "Region":
      return item?.locations ? itemLocation(item) : "-";
    case "P/ct":
      return item?.price_per_carat
        ? formatCurrency(item?.price_per_carat, {
            symbol: userCurrency,
            precision: 0,
          })
        : "-";
    case "discountPercent":
      return percent(item?.discount_percent);
    case "totalPrice":
      return item?.total_sales_price
        ? formatCurrency(item?.total_sales_price, {
            symbol: userCurrency,
            precision: 2,
          })
        : "-";
  }
};

const bodyStyleToCSS = (style: any) =>
  Object.entries(style)
    .map(([key, value]) => {
      const cssKey = key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
      const cssValue =
        typeof value === "number" && key !== "fontWeight"
          ? `${value}px`
          : value;
      return `${cssKey}: ${cssValue};`;
    })
    .join(" ");

export {
  calculateItemPrices,
  calculateParcelValues,
  addPercentage,
  removePercentage,
  shortTitle,
  generateSavedSearchName,
  generateJewelrySavedSearchName,
  generateGemstoneSavedSearchName,
  getProductId,
  getItemTypeColor,
  formatNumberWithPrecision,
  addComma,
  percent,
  getDiamondValue,
  formatCurrency,
  bodyStyleToCSS,
  handleDeepLink,
};
